// features/admin/shippingApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { adminrefreshToken } from '../../utils/adminRefreshToken.ts';
import {
  ShippingCharge as ShippingChargeType,
  ShippingApiResponse,
  AddShippingChargeRequest
} from '../../features/admin/types.ts';

export interface UpdateShippingChargeRequest {
  model_id: number;
  model_name: string;
  shipping_charge_condition: any;
  active: number;
  is_default: number;
  is_restricted: number;
}

export interface DeleteShippingChargeRequest {
  model_id: number;
}

export interface SetDefaultShippingChargeRequest {
  model_id: number;
}

export interface GetShippingChargeByIdRequest {
  model_id: number;
}

// Export the ShippingCharge type for use in other files
export type ShippingCharge = ShippingChargeType;

export const SHIPPING_API_CONFIG = {
  BASE_URL: 'https://fftestservice.sarsetech.com',
  ENDPOINTS: {
    LIST_SHIPPING_CHARGES: '/globalsettings/listShippingCharges',
    ADD_SHIPPING_CHARGE: '/globalsettings/newShippingCharge',
    UPDATE_SHIPPING_CHARGE: '/globalsettings/updateshippingcharge',
    DELETE_SHIPPING_CHARGE: '/globalsettings/deleteShippingChargeModel',
    GET_SHIPPING_CHARGE_BY_ID: '/globalsettings/get',
    SET_DEFAULT_SHIPPING_CHARGE: '/globalsettings/set-default',
  },
};

// Helper function to stringify shipping charge condition
const stringifyCondition = (condition: any): string => {
  if (typeof condition === 'string') {
    try {
      // If it's already a string, try to parse and re-stringify to ensure valid JSON
      const parsed = JSON.parse(condition);
      return JSON.stringify(parsed);
    } catch {
      return condition;
    }
  }
  return JSON.stringify(condition);
};

export const shippingApi = createApi({
  reducerPath: 'shippingApi',
  baseQuery: fetchBaseQuery({
    baseUrl: SHIPPING_API_CONFIG.BASE_URL,
    prepareHeaders: async (headers) => {
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      
      // Refresh token before each request if needed
      try {
        await adminrefreshToken();
      } catch (err) {
        console.error('Error refreshing token:', err);
      }

      // Retrieve the latest token from localStorage
      const token = localStorage.getItem('admin_token');

      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      // Set content type to JSON instead of form data
      headers.set('Content-Type', 'application/json');

      return headers;
    },
  }),
  tagTypes: ['ShippingCharge'],
  endpoints: (build) => ({
    listShippingCharges: build.query<ShippingApiResponse<ShippingCharge[]>, void>({
      query: () => ({
        url: SHIPPING_API_CONFIG.ENDPOINTS.LIST_SHIPPING_CHARGES,
        method: 'POST',
        body: {}, // Empty JSON object
      }),
      providesTags: ['ShippingCharge'],
      transformResponse: (response: any) => ({
        error: response.error || false,
        message: response.message || '',
        result: response.result || [],
      }),
    }),

    addShippingCharge: build.mutation<ShippingApiResponse<any>, AddShippingChargeRequest>({
      query: (data) => ({
        url: SHIPPING_API_CONFIG.ENDPOINTS.ADD_SHIPPING_CHARGE,
        method: 'POST',
        body: {
          model_name: data.model_name.trim(),
          shipping_charge_condition: stringifyCondition(data.shipping_charge_condition),
          active: data.active ?? 1,
          is_default: data.is_default ?? 0,
          is_restricted: data.is_restricted ?? 1,
        },
      }),
      invalidatesTags: ['ShippingCharge'],
      transformResponse: (response: any) => response,
    }),

    updateShippingCharge: build.mutation<ShippingApiResponse<any>, UpdateShippingChargeRequest>({
      query: (data) => ({
        url: SHIPPING_API_CONFIG.ENDPOINTS.UPDATE_SHIPPING_CHARGE,
        method: 'POST',
        body: {
          model_id: data.model_id,
          model_name: data.model_name.trim(),
          shipping_charge_condition: stringifyCondition(data.shipping_charge_condition),
          active: data.active ?? 1,
          is_default: data.is_default ?? 0,
          is_restricted: data.is_restricted ?? 1,
        },
      }),
      invalidatesTags: ['ShippingCharge'],
      transformResponse: (response: any) => response,
    }),

    deleteShippingCharge: build.mutation<ShippingApiResponse<any>, DeleteShippingChargeRequest>({
      query: ({ model_id }) => ({
        url: SHIPPING_API_CONFIG.ENDPOINTS.DELETE_SHIPPING_CHARGE,
        method: 'POST',
        body: {
          model_id: model_id,
        },
      }),
      invalidatesTags: ['ShippingCharge'],
      transformResponse: (response: any) => response,
    }),

    getShippingChargeById: build.query<ShippingApiResponse<ShippingCharge>, GetShippingChargeByIdRequest>({
      query: ({ model_id }) => ({
        url: SHIPPING_API_CONFIG.ENDPOINTS.GET_SHIPPING_CHARGE_BY_ID,
        method: 'POST',
        body: {
          model_id: model_id,
        },
      }),
      providesTags: ['ShippingCharge'],
      transformResponse: (response: any) => response,
    }),

    setDefaultShippingCharge: build.mutation<ShippingApiResponse<any>, SetDefaultShippingChargeRequest>({
      query: ({ model_id }) => ({
        url: SHIPPING_API_CONFIG.ENDPOINTS.SET_DEFAULT_SHIPPING_CHARGE,
        method: 'POST',
        body: {
          model_id: model_id,
        },
      }),
      invalidatesTags: ['ShippingCharge'],
      transformResponse: (response: any) => response,
    }),
  }),
});

export const {
  useListShippingChargesQuery,
  useAddShippingChargeMutation,
  useUpdateShippingChargeMutation,
  useDeleteShippingChargeMutation,
  useGetShippingChargeByIdQuery,
  useSetDefaultShippingChargeMutation,
} = shippingApi;